import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { FaHeart, FaDonate, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaBars, FaTimes, FaLanguage } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const MainLayout: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();

  const navigationItems = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/services', label: t('services') },
    { path: '/donation', label: t('donate') },
    { path: '/volunteer', label: t('volunteer') },
    { path: '/events', label: t('events') },
    { path: '/blog', label: t('blog') },
    { path: '/contact', label: t('contact') },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Header */}
      <div className="hidden md:block bg-primary-green text-white py-2">
        <div className="container-lg px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm"><FaPhoneAlt /></span>
                <span className="text-sm">(+91)- 8287381125, +91- 8448038935</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm"><FaEnvelope /></span>
                <span className="text-sm">vishwabandhuwf@gmail.com</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex space-x-2">
                <a href="https://www.facebook.com/p/Vishwa-Bandhu-Welfare-Foundation-100070113370205/" target="_blank" rel="noopener noreferrer" className="hover:text-primary-orange transition-colors">
                  <FaFacebook />
                </a>
                <a href="https://www.instagram.com/vishwabandhuwelfarefoundation/" target="_blank" rel="noopener noreferrer" className="hover:text-primary-orange transition-colors">
                  <FaInstagram />
                </a>

              </div>
              <Link to="/donation" className="btn-secondary text-sm py-1 px-3">
                <span className="inline mr-1"><FaDonate /></span> {t('donateNow')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="container-lg px-4">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/vishwa-bandhu-logo.png" alt="Vishwa Bandhu Logo" className="h-20 w-20 object-contain rounded-[20px]" />
              <div>
                <h1 className="text-2xl font-bold text-primary-green">Vishwa Bandhu</h1>
                <p className="text-xs text-gray-600">{t('welfareFoundation')}</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-medium transition-colors ${isActive(item.path)
                      ? 'text-primary-green border-b-2 border-primary-green'
                      : 'text-gray-700 hover:text-primary-green'
                    }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center space-x-4">
              <button 
                onClick={toggleLanguage}
                className="flex items-center justify-center px-3 py-2 rounded-full bg-green-50 text-primary-green hover:bg-primary-green hover:text-white transition-colors duration-300 shadow-sm border border-green-100"
                title="Switch Language"
              >
                <FaLanguage className="text-xl mr-1" />
                <span className="text-sm font-medium">{language === 'en' ? 'हिन्दी' : 'English'}</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden text-gray-700"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <span className="text-2xl"><FaTimes /></span> : <span className="text-2xl"><FaBars /></span>}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden overflow-hidden"
              >
                <div className="py-4 space-y-4">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`block font-medium py-2 ${isActive(item.path)
                          ? 'text-primary-green bg-green-50 rounded-lg px-4'
                          : 'text-gray-700 hover:text-primary-green px-4'
                        }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <div className="pt-4 border-t">
                    <Link to="/donation" className="btn-primary w-full block" onClick={() => setIsMenuOpen(false)}>
                      <span className="inline mr-2"><FaDonate /></span> {t('donateNow')}
                    </Link>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="container-lg px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* About Foundation */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src="/vishwa-bandhu-logo.png" alt="Vishwa Bandhu Logo" className="h-16 w-16 object-contain rounded-lg" />
                <div>
                  <h3 className="text-xl font-bold">Vishwa Bandhu</h3>
                  <p className="text-sm text-gray-300">{t('welfareFoundation')}</p>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                We are committed to building a better future through social welfare,
                environmental protection, and humanitarian services.
              </p>
              <div className="flex space-x-3">
                <a href="https://www.facebook.com/p/Vishwa-Bandhu-Welfare-Foundation-100070113370205/" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-primary-green p-2 rounded-full transition-colors">
                  <FaFacebook />
                </a>
                <a href="https://www.instagram.com/vishwabandhuwelfarefoundation/" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-primary-green p-2 rounded-full transition-colors">
                  <FaInstagram />
                </a>

              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('quickLinks')}</h4>
              <ul className="space-y-3">
                {navigationItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className="text-gray-300 hover:text-primary-orange transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Our Services */}
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('ourServices')}</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-gray-300 hover:text-primary-orange transition-colors">
                    Tree Plantation
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-300 hover:text-primary-orange transition-colors">
                    Cow Welfare
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-300 hover:text-primary-orange transition-colors">
                    Girls Education
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-300 hover:text-primary-orange transition-colors">
                    Natural Disaster Relief
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-300 hover:text-primary-orange transition-colors">
                    Old Age Care
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('contactUs')}</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <span className="text-primary-orange mt-1"><FaMapMarkerAlt /></span>
                  <p className="text-gray-300">
                    A-208, BlockA, Sector 43<br />
                    Noida Uttar Pradesh- 201303 INDIA
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-primary-orange"><FaPhoneAlt /></span>
                  <p className="text-gray-300">(+91)- 8287381125, +91- 8448038935</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-primary-orange"><FaEnvelope /></span>
                  <p className="text-gray-300">vishwabandhuwf@gmail.com</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Vishwa Bandhu Welfare Foundation. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Terms of Service
                </a>
                <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                  Disclaimer
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;